import { projects } from "../../../../../db";
import {
  calculateDurationInMonths,
  calculateProfessionalAttributedProjectDuration,
  normalizeSkillFormat,
  createSkillMaps,
  addSkillToMap,
  normalizeSkillMaps,
  type SkillMaps,
  type NormalizedSkillsByCategory
} from './skillCalculationUtils';
import { fetchGitHubLanguages, isOpenSourceGitHubRepo } from '../github/githubLanguages';

interface PrivateSkillsByCategory {
  profession: Array<{ name: string; level: number; }>;
  lead: Array<{ name: string; level: number; }>;
  language: Array<{ name: string; level: number; }>;
  tools: Array<{ name: string; level: number; }>;
}

interface EnhancedPrivateProject {
  id: number;
  title: string;
  categorizedSkills?: any;
  skills?: any[];
  tech?: any[];
  git?: string;
  startDate: string;
  endDate: string | null;
  activityId: number | number[] | null;
  githubLanguages?: Array<{ name: string; weight: number; }>;
}

// Cache for GitHub-enhanced private projects
const githubEnhancedPrivateProjectsCache = new Map<string, EnhancedPrivateProject[]>();
const privateGithubLanguageCache = new Map<string, Array<{ name: string; weight: number; }>>();

/**
 * Enhance a private project with GitHub language data
 */
async function enhancePrivateProjectWithGitHub(project: any): Promise<EnhancedPrivateProject> {
  const enhancedProject: EnhancedPrivateProject = { ...project };
  
  // Only enhance projects that have GitHub links
  if (isOpenSourceGitHubRepo(project.git)) {
    const cacheKey = project.git;
    
    // Check cache first
    if (privateGithubLanguageCache.has(cacheKey)) {
      enhancedProject.githubLanguages = privateGithubLanguageCache.get(cacheKey);
    } else {
      try {
        // Fetch GitHub languages
        const githubLanguages = await fetchGitHubLanguages(project.git);
        const formattedLanguages = githubLanguages.map(lang => ({
          name: lang.name,
          weight: lang.weight
        }));
        
        // Cache the result
        privateGithubLanguageCache.set(cacheKey, formattedLanguages);
        enhancedProject.githubLanguages = formattedLanguages;
      } catch (error) {
        console.warn(`Failed to enhance private project ${project.title} with GitHub data:`, error);
        enhancedProject.githubLanguages = [];
      }
    }
  }
  
  return enhancedProject;
}

/**
 * Get normalized skills for a private project, with GitHub languages replacing static language skills
 */
function getNormalizedPrivateSkillsWithGitHub(project: EnhancedPrivateProject) {
  // Start with normal skill normalization
  const normalizedSkills = normalizeSkillFormat(project);
  
  // If we have GitHub languages, replace the language category
  if (project.githubLanguages && project.githubLanguages.length > 0) {
    normalizedSkills.language = project.githubLanguages;
  }
  
  return normalizedSkills;
}

/**
 * Calculate private project skills with GitHub language enhancement
 */
export async function calculateGitHubEnhancedPrivateSkills(): Promise<PrivateSkillsByCategory> {
  // Get only private projects
  const privateProjects = projects.filter((project: any) => project.activityId === null);
  
  if (privateProjects.length === 0) {
    return {
      profession: [],
      lead: [],
      language: [],
      tools: []
    };
  }

  const cacheKey = `private-enhanced-${privateProjects.length}`;
  
  // Check if we have cached enhanced projects
  if (githubEnhancedPrivateProjectsCache.has(cacheKey)) {
    const enhancedProjects = githubEnhancedPrivateProjectsCache.get(cacheKey)!;
    return calculateSkillsFromEnhancedPrivateProjects(enhancedProjects);
  }

  // Enhance all private projects with GitHub data in parallel
  const enhancementPromises = privateProjects.map(project => enhancePrivateProjectWithGitHub(project));
  const enhancedProjects = await Promise.all(enhancementPromises);
  
  // Cache the enhanced projects
  githubEnhancedPrivateProjectsCache.set(cacheKey, enhancedProjects);
  
  return calculateSkillsFromEnhancedPrivateProjects(enhancedProjects);
}

/**
 * Calculate skills from enhanced private projects
 */
function calculateSkillsFromEnhancedPrivateProjects(enhancedProjects: EnhancedPrivateProject[]): PrivateSkillsByCategory {
  const skillMaps = createSkillMaps();

  // Calculate total duration of private projects for normalization
  const totalPrivateProjectMonths = enhancedProjects.reduce((total, project) => {
    const duration = calculateDurationInMonths(project.startDate, project.endDate);
    return total + duration;
  }, 0);

  // Process all enhanced private projects
  enhancedProjects.forEach((project) => {
    // Use simple duration calculation for private projects (no multi-activity splitting needed)
    const duration = calculateDurationInMonths(project.startDate, project.endDate);
    
    // Get normalized skill format with GitHub language enhancement
    const normalizedSkills = getNormalizedPrivateSkillsWithGitHub(project);
    
    // Process all skill categories
    Object.entries(normalizedSkills).forEach(([category, skills]) => {
      const categoryKey = category as keyof SkillMaps;
      skills.forEach((skill: any) => {
        if (!skill.name || typeof skill.weight !== 'number') return;
        
        addSkillToMap(skillMaps, categoryKey, skill.name, duration, skill.weight);
      });
    });
  });

  // Calculate normalized skill levels (0-100) for each category
  const skillsByCategory = normalizeSkillMaps(skillMaps, totalPrivateProjectMonths);
  
  return skillsByCategory;
}

/**
 * Get private skills with fallback to static calculation
 */
export async function getPrivateSkillsWithGitHubFallback(): Promise<PrivateSkillsByCategory> {
  try {
    return await calculateGitHubEnhancedPrivateSkills();
  } catch (error) {
    console.warn('GitHub-enhanced private skills calculation failed, falling back to static calculation:', error);
    return calculateStaticPrivateSkills();
  }
}

/**
 * Calculate private project skills using only static data (fallback)
 */
export function calculateStaticPrivateSkills(): PrivateSkillsByCategory {
  const skillMaps = createSkillMaps();
  
  // Get only private projects
  const privateProjects = projects.filter((project: any) => project.activityId === null);
  
  if (privateProjects.length === 0) {
    return {
      profession: [],
      lead: [],
      language: [],
      tools: []
    };
  }

  // Calculate total duration of private projects for normalization
  const totalPrivateProjectMonths = privateProjects.reduce((total, project) => {
    const duration = calculateDurationInMonths(project.startDate, project.endDate);
    return total + duration;
  }, 0);

  // Process private projects
  privateProjects.forEach((project: any) => {
    const duration = calculateDurationInMonths(project.startDate, project.endDate);
    
    // Get normalized skill format
    const normalizedSkills = normalizeSkillFormat(project);
    
    // Process all skill categories
    Object.entries(normalizedSkills).forEach(([category, skills]) => {
      const categoryKey = category as keyof SkillMaps;
      skills.forEach((skill: any) => {
        if (!skill.name || typeof skill.weight !== 'number') return;
        
        addSkillToMap(skillMaps, categoryKey, skill.name, duration, skill.weight);
      });
    });
  });

  // Calculate normalized skill levels
  const skillsByCategory = normalizeSkillMaps(skillMaps, totalPrivateProjectMonths);
  
  return skillsByCategory;
}

/**
 * Get total private skills count
 */
export function getTotalPrivateSkillsCount(): Promise<number> {
  return getPrivateSkillsWithGitHubFallback().then(skills => 
    Object.values(skills).reduce((total, categorySkills) => total + categorySkills.length, 0)
  );
}

/**
 * Clear private GitHub language cache (useful for development/testing)
 */
export function clearPrivateGitHubLanguageCache(): void {
  privateGithubLanguageCache.clear();
  githubEnhancedPrivateProjectsCache.clear();
}
